import { useEffect, useRef } from 'react';
import { apiUrl } from '../lib/api-base';
import { SCORING_POLL_MS, scoringWebSocketUrl, summaryFormat, type PublicLeaderboard } from '../lib/shooting';
import type { TournamentDetail } from './useScoringTournament';

/** Update standings and tournament status; saved score rows and editor drafts have a separate lifecycle. */
export function mergeScoringLiveBoard(detail: TournamentDetail, board: PublicLeaderboard): TournamentDetail {
  const format = summaryFormat(detail.tournament);
  if (board.tournament.slug !== detail.tournament.slug || board.tournament.id !== detail.tournament.id) return detail;
  const tournament = {
    ...detail.tournament,
    status: detail.tournament.status === 'final' ? 'final' : board.tournament.status ?? detail.tournament.status,
  };
  if (board.tournament.shotsPlanned !== format.shotsPerShooter || board.tournament.shotsCounted !== format.countedShots) {
    return board.tournament.status === 'final' ? { ...detail, tournament } : detail;
  }
  const oldRows = new Map(detail.leaderboard.rows.map((row) => [row.shooterId, row]));
  const oldTeams = new Map(detail.leaderboard.round1?.teams.map((team) => [team.teamId, team]) ?? []);
  const round1 = board.round1 ? {
    ...detail.leaderboard.round1,
    ...board.round1,
    teams: board.round1.teams.map((team) => {
      const old = oldTeams.get(team.teamId);
      const members = new Map(old?.members?.map((member) => [member.shooterId, member]) ?? []);
      return { ...old, ...team, members: team.members.map((member) => ({ ...members.get(member.shooterId), ...member })) };
    }),
  } : detail.leaderboard.round1;
  return {
    ...detail,
    tournament,
    leaderboard: {
      ...detail.leaderboard,
      rows: board.rows.map((row) => ({ ...oldRows.get(row.shooterId), ...row })),
      updatedAt: board.updatedAt,
      displayPage: board.displayPage,
      round1,
    },
  };
}

/** Existing TV broadcasts carry authoritative standings after background score publishing. */
export function subscribeScoringLiveBoard(slug: string, onBoard: (board: PublicLeaderboard) => void): () => void {
  let socket: WebSocket | null = null;
  let stopped = false;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let attempt = 0;
  // Score timestamps may go backwards after a delete; socket delivery order is authoritative.
  let messageRevision = 0;
  let pulling = false;
  const controller = new AbortController();
  const accept = (board: PublicLeaderboard) => {
    if (stopped || !board?.ok || board.tournament?.slug !== slug || !Array.isArray(board.rows)) return false;
    onBoard(board);
    return true;
  };
  const pull = async () => {
    if (stopped || pulling) return;
    pulling = true;
    const startedAtRevision = messageRevision;
    try {
      const response = await fetch(apiUrl(`/api/public/shooting/${encodeURIComponent(slug)}`), {
        cache: 'no-store', credentials: 'omit', signal: controller.signal,
      });
      if (response.ok) {
        const snapshot = await response.json() as PublicLeaderboard;
        if (startedAtRevision === messageRevision) accept(snapshot);
      }
    } catch { /* Retain the last standings and let the next poll/reconnect retry. */ }
    finally { pulling = false; }
  };
  const stopPoll = () => {
    if (pollTimer != null) clearInterval(pollTimer);
    pollTimer = null;
  };
  const startPoll = () => {
    if (stopped || pollTimer != null) return;
    void pull();
    pollTimer = setInterval(() => void pull(), SCORING_POLL_MS);
  };
  const connect = () => {
    if (stopped) return;
    reconnectTimer = null;
    let candidate: WebSocket;
    const disconnected = () => {
      if (stopped || socket !== candidate) return;
      socket = null;
      startPoll();
      reconnectTimer = setTimeout(connect, Math.min(8000, 1000 * 2 ** attempt++));
    };
    try {
      candidate = new WebSocket(scoringWebSocketUrl(apiUrl(`/api/public/shooting/${encodeURIComponent(slug)}/live`)));
      socket = candidate;
      candidate.onopen = () => {
        if (stopped || socket !== candidate) return;
        attempt = 0;
        stopPoll();
      };
      candidate.onmessage = (event) => {
        if (stopped || socket !== candidate) return;
        try {
          const message = JSON.parse(String(event.data)) as { type?: string; leaderboard?: PublicLeaderboard };
          if (message.type === 'leaderboard' && message.leaderboard && accept(message.leaderboard)) messageRevision += 1;
        } catch { /* Ignore malformed messages. */ }
      };
      candidate.onerror = () => { candidate.close(); disconnected(); };
      candidate.onclose = disconnected;
    } catch {
      startPoll();
      reconnectTimer = setTimeout(connect, Math.min(8000, 1000 * 2 ** attempt++));
    }
  };
  startPoll();
  connect();
  return () => {
    stopped = true;
    controller.abort();
    stopPoll();
    if (reconnectTimer != null) clearTimeout(reconnectTimer);
    socket?.close();
    socket = null;
  };
}

export function useScoringLiveBoard(slug: string, enabled: boolean, onBoard: (board: PublicLeaderboard) => void): void {
  const callback = useRef(onBoard);
  callback.current = onBoard;
  useEffect(() => enabled ? subscribeScoringLiveBoard(slug, (board) => callback.current(board)) : undefined, [slug, enabled]);
}
